/**
 * 底图源目录 —— 在线、离线、需要密钥的自定义源用**同一套描述**。
 *
 * ## 为什么是一个数据目录而不是一堆 if
 *
 * "可插拔"在界面上的意思很具体：切换底图只是换一份描述，**车辆图层一行都不动**。
 * 因此源描述里不允许出现任何"图层"概念 —— 它只说"瓦片从哪来、怎么拼 URL、
 * 署名怎么写、要不要密钥"。谁去取这些瓦片（缓存优先、断网时退回缓存）
 * 是 `tileProtocol.ts` 的事，两者必须能独立变化。
 *
 * ## 默认源必须**不需要密钥**
 *
 * 本应用是纯前端、无后端，用户可能在任何一台机器上打开它。默认源一旦需要密钥，
 * 第一次打开看到的就是一片空白和一行"请填写 API key" —— 那不是"可选功能缺失"，
 * 那是"软件坏了"。所以默认是 OSM 标准瓦片（无密钥），需要密钥的源一律
 * 显式标注 `requiresKey` 并给出申请地址，未填密钥时**不参与**底图切换（见
 * `sourceReadiness`）。
 *
 * ## 署名不是可选项
 *
 * 各家的使用条款都要求署名，`attribution` 因此是必填字段而不是可选装饰。
 */

import type { LatLon } from '@hgc/ardupilot';
import { expandTileUrl, type TileScheme } from './tiles';

export interface BaseMapSource {
  /** 稳定标识。同时作为缓存键前缀与 `hgc://tiles/<id>/…` 的路径段，只允许 `[a-z0-9._-]`。 */
  readonly id: string;
  /** 界面上的短名（中文） */
  readonly name: string;
  /** 一句话说明：数据是什么、用起来有什么代价 */
  readonly description: string;
  /** 瓦片 URL 模板。多条时按 MapLibre 的 `(x + y) % n` 轮转（用于子域分流） */
  readonly tiles: readonly string[];
  readonly minZoom: number;
  readonly maxZoom: number;
  readonly tileSize: 256 | 512;
  readonly scheme: TileScheme;
  /** 模板里 `{s}` 的取值；空数组表示不需要 */
  readonly subdomains: readonly string[];
  /** 必须显示在界面上的署名（使用条款要求） */
  readonly attribution: string;
  /** 是否必须由用户提供密钥。为 `true` 时模板里应含 `{key}` */
  readonly requiresKey: boolean;
  /** 密钥在界面上的叫法，例如"天地图密钥" */
  readonly keyLabel?: string;
  /** 密钥申请地址，直接告诉用户去哪拿 */
  readonly keyHelpUrl?: string;
  /** 使用条款上的注意事项（例如"服务端缓存受限""仅限个人使用"） */
  readonly licenseNote?: string;
  /** 预缓存时默认覆盖的缩放范围；缺省用 `[minZoom, maxZoom]` 与当前视野 */
  readonly prefetchZoom?: { readonly min: number; readonly max: number };
}

const SOURCE_ID_PATTERN = /^[a-z0-9][a-z0-9._-]*$/;

/**
 * 默认源：OpenStreetMap 标准瓦片。
 *
 * 选它作为默认的唯一理由是"**无密钥即可用**"。它的使用条款明确限制批量下载，
 * 因此这里顺带把 `licenseNote` 写清楚，并且引擎的预缓存默认走用户显式点击
 * （而不是自动全量抓取）—— 自动抓取会直接违反条款，也会在计费源上产生账单。
 */
const OSM: BaseMapSource = {
  id: 'osm',
  name: '开放街图（OSM）',
  description: '路网与地名最全的通用底图，无需密钥',
  tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
  minZoom: 0,
  maxZoom: 19,
  tileSize: 256,
  scheme: 'xyz',
  subdomains: [],
  attribution: '© OpenStreetMap 贡献者',
  requiresKey: false,
  licenseNote:
    'OSM 瓦片服务禁止批量抓取，仅适合正常浏览；大规模预缓存请改用自建瓦片服务或商业源。',
  prefetchZoom: { min: 10, max: 16 },
};

const OPEN_TOPO_MAP: BaseMapSource = {
  id: 'opentopomap',
  name: 'OpenTopoMap（地形）',
  description: '等高线与地形晕渲，山地作业时比路网图更有用；无需密钥',
  tiles: ['https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png'],
  minZoom: 0,
  maxZoom: 17,
  tileSize: 256,
  scheme: 'xyz',
  subdomains: ['a', 'b', 'c'],
  attribution: '© OpenTopoMap（CC-BY-SA）、© OpenStreetMap 贡献者',
  requiresKey: false,
};

const ESRI_IMAGERY: BaseMapSource = {
  id: 'esri-imagery',
  name: 'Esri 卫星影像',
  description: '全球卫星影像，无需密钥；注意模板里的顺序是 {z}/{y}/{x}',
  tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
  minZoom: 0,
  maxZoom: 19,
  tileSize: 256,
  scheme: 'xyz',
  subdomains: [],
  attribution: 'Esri、Maxar、Earthstar Geographics 及 GIS 用户社区',
  requiresKey: false,
  licenseNote: '仅限非商业用途；商用需向 Esri 购买授权。',
};

const TIANDITU_IMAGERY: BaseMapSource = {
  id: 'tianditu-img',
  name: '天地图影像',
  description: '国内影像底图，需自备天地图密钥（免费申请）',
  tiles: [
    'https://t{s}.tianditu.gov.cn/img_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0' +
      '&LAYER=img&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles' +
      '&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk={key}',
  ],
  minZoom: 1,
  maxZoom: 18,
  tileSize: 256,
  scheme: 'xyz',
  subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
  attribution: '© 天地图（国家地理信息公共服务平台）',
  requiresKey: true,
  keyLabel: '天地图密钥（tk）',
  keyHelpUrl: 'https://console.tianditu.gov.cn/api/key',
  licenseNote: '需在天地图控制台为密钥绑定域名/来源；有每日调用配额。',
};

const TIANDITU_VECTOR: BaseMapSource = {
  id: 'tianditu-vec',
  name: '天地图矢量',
  description: '国内矢量底图（中文注记），需自备天地图密钥',
  tiles: [
    'https://t{s}.tianditu.gov.cn/vec_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0' +
      '&LAYER=vec&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles' +
      '&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk={key}',
  ],
  minZoom: 1,
  maxZoom: 18,
  tileSize: 256,
  scheme: 'xyz',
  subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
  attribution: '© 天地图（国家地理信息公共服务平台）',
  requiresKey: true,
  keyLabel: '天地图密钥（tk）',
  keyHelpUrl: 'https://console.tianditu.gov.cn/api/key',
};

const MAPBOX_SATELLITE: BaseMapSource = {
  id: 'mapbox-satellite',
  name: 'Mapbox 卫星影像',
  description: '商业影像底图，需自备 Mapbox access token（按流量计费）',
  tiles: [
    'https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/256/{z}/{x}/{y}@2x?access_token={key}',
  ],
  minZoom: 0,
  maxZoom: 22,
  tileSize: 512,
  scheme: 'xyz',
  subdomains: [],
  attribution: '© Mapbox、© Maxar',
  requiresKey: true,
  keyLabel: 'Mapbox access token',
  keyHelpUrl: 'https://account.mapbox.com/access-tokens/',
  licenseNote: '按瓦片请求计费；离线预缓存受其条款限制，使用前请确认。',
};

/**
 * 出厂源目录。顺序即界面顺序：默认源排第一。
 */
export const DEFAULT_SOURCES: readonly BaseMapSource[] = Object.freeze([
  OSM,
  OPEN_TOPO_MAP,
  ESRI_IMAGERY,
  TIANDITU_IMAGERY,
  TIANDITU_VECTOR,
  MAPBOX_SATELLITE,
]);

export const DEFAULT_SOURCE_ID = OSM.id;

export function isSourceId(value: string): boolean {
  return SOURCE_ID_PATTERN.test(value);
}

/**
 * 用户自填底图源。
 *
 * 这是"可插拔"里真正通用的那一半：任何 XYZ 瓦片服务（自建、内网、商业）
 * 只要能写成一个 URL 模板就能用。模板里出现 `{key}` 就自动要求密钥 ——
 * 由模板本身推断，而不是让用户再去勾一个容易漏掉的选项。
 */
export function createCustomSource(input: {
  readonly id: string;
  readonly name: string;
  readonly urlTemplate: string;
  readonly minZoom?: number;
  readonly maxZoom?: number;
  readonly tileSize?: 256 | 512;
  readonly scheme?: TileScheme;
  readonly subdomains?: readonly string[];
  readonly attribution?: string;
  readonly description?: string;
}): BaseMapSource {
  if (!isSourceId(input.id)) {
    throw new Error(`底图源 id 非法：${input.id}（只允许小写字母、数字、. _ -，且不以符号开头）`);
  }
  if (!/^https?:\/\//.test(input.urlTemplate)) {
    throw new Error(`底图源 ${input.id} 的 URL 模板必须以 http(s):// 开头`);
  }
  const minZoom = input.minZoom ?? 0;
  const maxZoom = input.maxZoom ?? 19;
  return {
    id: input.id,
    name: input.name,
    description: input.description ?? '自定义底部图源',
    tiles: [input.urlTemplate],
    minZoom,
    maxZoom,
    tileSize: input.tileSize ?? 256,
    scheme: input.scheme ?? 'xyz',
    subdomains: input.subdomains ?? [],
    attribution: input.attribution ?? input.name,
    requiresKey: input.urlTemplate.includes('{key}'),
    licenseNote: '自定义源的使用条款由提供方决定，请自行确认。',
    prefetchZoom: { min: minZoom, max: maxZoom },
  };
}

export function sourceForTile(source: BaseMapSource, tile: { readonly x: number; readonly y: number }): string | null {
  if (source.tiles.length === 0) return null;
  return source.tiles[Math.abs(tile.x + tile.y) % source.tiles.length] ?? null;
}

/**
 * 某块瓦片的真实上游 URL。
 *
 * 返回 `null` 的两种情况都必须处理成"没有底图可显示"而不是"发一个坏请求"：
 * 需要密钥但还没填、以及模板里有我们替换不掉的占位符（例如模板写错了）。
 */
export function resolveTileUrl(
  source: BaseMapSource,
  tile: { readonly z: number; readonly x: number; readonly y: number },
  key: string | null,
  pixelRatio = 1,
): string | null {
  const template = sourceForTile(source, tile);
  if (template === null) return null;
  return expandTileUrl(template, tile, {
    scheme: source.scheme,
    subdomains: source.subdomains,
    key,
    pixelRatio,
  });
}

export type SourceReadiness =
  | { readonly ready: true }
  | { readonly ready: false; readonly reason: string };

/** 这个源现在能不能用；不能用时给出**可以直接照做**的中文理由。 */
export function sourceReadiness(source: BaseMapSource, key: string | null): SourceReadiness {
  if (!source.requiresKey) return { ready: true };
  if (key !== null && key.trim() !== '') return { ready: true };
  const label = source.keyLabel ?? '访问密钥';
  const where = source.keyHelpUrl === undefined ? '' : `（申请地址：${source.keyHelpUrl}）`;
  return { ready: false, reason: `需要${label}：请先填入密钥${where}` };
}

/**
 * 源目录。
 *
 * 做成对象而不是模块级数组，是为了让测试能各自建一份、互不影响；
 * 应用层用下面那个单例 `defaultSourceCatalog`，其它功能（例如 `map-draw` 的
 * 栅格叠加层）可以往里注册自己的源。
 */
export interface SourceCatalog {
  list(): readonly BaseMapSource[];
  get(id: string): BaseMapSource | undefined;
  /** 注册或按 id 覆盖一个源；`id` 非法时抛错（早失败好过缓存键悄悄错位） */
  register(source: BaseMapSource): void;
  remove(id: string): void;
}

export function createSourceCatalog(initial: readonly BaseMapSource[] = []): SourceCatalog {
  const sources = new Map<string, BaseMapSource>();
  for (const source of initial) sources.set(source.id, source);
  return {
    list(): readonly BaseMapSource[] {
      return [...sources.values()];
    },
    get(id: string): BaseMapSource | undefined {
      return sources.get(id);
    },
    register(source: BaseMapSource): void {
      if (!isSourceId(source.id)) {
        throw new Error(`底图源 id 非法：${source.id}`);
      }
      sources.set(source.id, source);
    },
    remove(id: string): void {
      sources.delete(id);
    },
  };
}

/** 应用级共享目录。注册进去的源会出现在底图选择器里。 */
export const defaultSourceCatalog: SourceCatalog = createSourceCatalog(DEFAULT_SOURCES);

/* ───────────────────────── 密钥存放 ───────────────────────── */

export const SOURCE_KEY_STORAGE_PREFIX = 'hgc.map.sourceKey.';

/**
 * 密钥只存在本机 `localStorage`。
 *
 * 这是纯前端应用的**唯一**选择：没有后端就没有别的地方可放。因此不在界面里
 * 承诺"密钥很安全" —— 只说清它存在哪、谁会看到（同源脚本都能读）。
 * 读取一律包 `try`：隐私模式下 `localStorage` 会直接抛，而"读不出密钥"
 * 只应该退化成"这个源暂时用不了"，不该让整个应用起不来。
 */
export function readSourceKey(sourceId: string): string | null {
  try {
    const raw = window.localStorage.getItem(`${SOURCE_KEY_STORAGE_PREFIX}${sourceId}`);
    return raw === null || raw.trim() === '' ? null : raw;
  } catch {
    return null;
  }
}

export function writeSourceKey(sourceId: string, key: string): void {
  try {
    const trimmed = key.trim();
    if (trimmed === '') window.localStorage.removeItem(`${SOURCE_KEY_STORAGE_PREFIX}${sourceId}`);
    else window.localStorage.setItem(`${SOURCE_KEY_STORAGE_PREFIX}${sourceId}`, trimmed);
  } catch {
    /* 存不下只影响下次启动要重填，不影响本次会话 */
  }
}

/** 某个源在界面上要不要显示"缺密钥"的提示。 */
export function missingKeyReason(source: BaseMapSource, key: string | null): string | null {
  const readiness = sourceReadiness(source, key);
  return readiness.ready ? null : readiness.reason;
}

/** 供底图选择器使用：某个源有没有密钥（读 localStorage，不抛错）。 */
export function hasSourceKey(sourceId: string): boolean {
  const key = readSourceKey(sourceId);
  return key !== null && key.trim() !== '';
}

/** 无定位信息时的初始视角：全球（不猜任何位置，避免把用户带到错误的大陆）。 */
export const WORLD_CENTER: LatLon = Object.freeze({ latDeg: 20, lonDeg: 0 });

export const WORLD_ZOOM = 1.6;
